import React from 'react'

import { toast, ToastContainer, type ToastOptions } from 'react-toastify'

import { ToastContent } from './toast-content'
import { TOAST_VARIANT_STYLES, type ToastVariant } from './variants'

export interface NotifyParams {
  message: string
  type: string
  title?: string
  options?: ToastOptions
}

const AUTO_CLOSE_TIME = 5000

const toastOptions: ToastOptions = {
  hideProgressBar: true,
  closeOnClick: false,
  pauseOnHover: true,
  draggable: false,
  closeButton: false,
  icon: false,
  className: '!bg-transparent !shadow-none !p-0 !min-h-0',
}

export const toastTypes = {
  DEFAULT: 'default',
  SUCCESS: 'success',
  ERROR: 'error',
  WARNING: 'warning',
} as const

const TYPE_TO_VARIANT: Record<string, ToastVariant> = {
  [toastTypes.DEFAULT]: 'info',
  [toastTypes.SUCCESS]: 'success',
  [toastTypes.ERROR]: 'error',
  [toastTypes.WARNING]: 'warning',
}

const resolveVariant = (type: string): ToastVariant => {
  return TYPE_TO_VARIANT[type] ?? 'info'
}

export class Toast {
  public static toastType: typeof toastTypes = toastTypes

  static notify({ message, type, title, options }: NotifyParams): void {
    const variant = resolveVariant(type)
    const resolvedTitle = title ?? TOAST_VARIANT_STYLES[variant].defaultTitle

    toast(
      ({ closeToast }) => (
        <ToastContent
          variant={variant}
          title={resolvedTitle}
          message={message}
          closeToast={closeToast}
        />
      ),
      { ...toastOptions, ...options },
    )
  }

  static Provider(): React.JSX.Element {
    return (
      <ToastContainer
        position="top-right"
        autoClose={AUTO_CLOSE_TIME}
        hideProgressBar
        newestOnTop={false}
        closeOnClick={false}
        rtl={false}
        pauseOnFocusLoss
        draggable={false}
        pauseOnHover
        theme="light"
        icon={false}
        closeButton={false}
        toastClassName="!bg-transparent !shadow-none !p-0 !min-h-0"
        bodyClassName="!p-0"
      />
    )
  }
}
